// Pantallas y luces en 3D: OLED (0.96″, 1.3″ y 0.91″), displays de 7 segmentos, módulo TM1637, matrices
// MAX7219 (FC-16) y display de 8 dígitos, NeoPixel (LED de 5 mm, anillo, tira y matriz) y barra de 10 LEDs.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, glow, setGlow } from '../kit.js';
import { PITCH, getPins } from '../../parts/registry.js';
import { LED_COLORS } from '../../parts/basic.js';
import {
  OLED, OLED13, OLED32, OLED_COLORS, SEG7, SEG7X4, TM1637, MAX7219, MAX7SEG, NEO, NEO_RING_SIZES, NEO_STICK, NEO_MATRIX, NEO_WHITES, BAR, BAR_COLORS, neoCount, neoRgbw,
} from '../../parts/displays.js';
import { Palette, Halos, linear, ledEmission, perceived, digitShapes, flatShape, flatDisc, maleHeaderUp, rightAngleHeader, BODY } from './displays-common.js';

const SILK = '#f1f4f2';

/** Anillos de estaño alrededor de agujeros de montaje. */
function holeRings(b, holes, y) {
  for (const [x, z, r] of holes) b.add(new THREE.RingGeometry(r, r + 0.9, 20).rotateX(-Math.PI / 2), M.tin, { x, y: y + 0.02, z });
}

/**
 * Vista de un modelo con paleta (y halos opcionales): `paint(v)` llena la paleta y los halos y aquí se
 * confirman; reset apaga todo.
 */
function paletteView(b, pal, paint, halos = null) {
  return {
    root: b.root,
    update(v) {
      paint(v);
      pal.commit();
      if (halos) halos.commit();
    },
    reset() {
      pal.clear();
      if (halos) halos.clear();
    },
    dispose() {
      pal.dispose();
      if (halos) halos.dispose();
    },
  };
}

// OLED ------------------------------------------------------------------------------------------------
const OLED_SCALE = 5; // píxeles del lienzo por píxel del panel (4 encendidos + 1 de separación)

class OledPainter {
  constructor(colorKey, rows = 64) {
    const c = OLED_COLORS[colorKey] || OLED_COLORS.white;
    this.rows = rows;
    this.split = !!c.top && rows === 64;
    this.on = new THREE.Color(c.hex);
    this.onTop = new THREE.Color(c.top || c.hex);
    this.small = document.createElement('canvas');
    this.small.width = 128;
    this.small.height = rows;
    this.sg = this.small.getContext('2d');
    this.img = this.sg.createImageData(128, rows);
    this.cv = document.createElement('canvas');
    this.cv.width = 128 * OLED_SCALE;
    this.cv.height = rows * OLED_SCALE;
    this.g = this.cv.getContext('2d');
    this.g.imageSmoothingEnabled = false;
    this.grid = this.makeGrid();
    this.tex = new THREE.CanvasTexture(this.cv);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.anisotropy = 8;
  }

  /** Separación oscura entre píxeles (y la franja entre la zona amarilla y la azul). */
  makeGrid() {
    const cv = document.createElement('canvas');
    cv.width = this.cv.width;
    cv.height = this.cv.height;
    const g = cv.getContext('2d');
    g.fillStyle = 'rgba(0,0,0,0.88)';
    for (let x = 0; x < 128; x++) g.fillRect(x * OLED_SCALE + OLED_SCALE - 1, 0, 1, cv.height);
    for (let y = 0; y < this.rows; y++) g.fillRect(0, y * OLED_SCALE + OLED_SCALE - 1, cv.width, 1);
    if (this.split) {
      g.fillStyle = '#000';
      g.fillRect(0, 16 * OLED_SCALE - 2, cv.width, 3);
    }
    return cv;
  }

  paint(v) {
    const d = this.img.data;
    const lit = v && v.on && v.img;
    const k = lit ? 0.42 + 0.58 * (v.contrast / 255) : 0;
    const cols = [this.on, this.onTop].map((c) => [c.r, c.g, c.b].map((x) => Math.round(255 * Math.pow(x, 1 / 2.2) * k)));
    for (let y = 0; y < this.rows; y++) {
      const [r, g, b] = cols[this.split && y < 16 ? 1 : 0];
      for (let x = 0; x < 128; x++) {
        const i = (y * 128 + x) * 4;
        const on = lit && (v.img[y * 16 + (x >> 3)] >> (7 - (x & 7))) & 1;
        d[i] = on ? r : 6;
        d[i + 1] = on ? g : 8;
        d[i + 2] = on ? b : 11;
        d[i + 3] = 255;
      }
    }
    this.sg.putImageData(this.img, 0, 0);
    this.g.drawImage(this.small, 0, 0, this.cv.width, this.cv.height);
    this.g.drawImage(this.grid, 0, 0);
    this.tex.needsUpdate = true;
  }
}

/**
 * Panel OLED: zona activa (lienzo con MeshBasicMaterial, luz propia) y encima el reflejo del vidrio
 * como capa aditiva (no oscurece los píxeles encendidos). Devuelve la vista.
 */
function oledPanel(b, painter, { active, x, z, top, glassW, glassD, glassX, glassZ }) {
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(active.w, active.d).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: painter.tex }));
  screen.position.set(x, top + 0.01, z);
  b.root.add(screen);
  const shine = new THREE.Mesh(
    new THREE.PlaneGeometry(glassW, glassD).rotateX(-Math.PI / 2),
    new THREE.MeshStandardMaterial({ color: '#000000', roughness: 0.32, metalness: 0, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false }),
  );
  shine.position.set(glassX, top + 0.02, glassZ);
  shine.userData.noPick = true;
  b.root.add(shine);
  painter.paint(null);
  return {
    root: b.root,
    update(v) {
      painter.paint(v);
    },
    reset() {
      painter.paint(null);
    },
    dispose() {
      painter.tex.dispose();
    },
  };
}

function oledSilk(L, labels) {
  const { W, D } = L;
  const { c, g } = canvas(W, D, 24);
  const X = (x) => x + W / 2;
  const Y = (z) => z + D / 2;
  g.fillStyle = '#163f8f';
  g.fillRect(0, 0, W, D);
  // Pistas bajo la máscara
  g.strokeStyle = 'rgba(8,36,96,0.75)';
  g.lineWidth = 0.32;
  for (let i = 0; i < 4; i++) {
    g.beginPath();
    g.moveTo(X((i - 1.5) * PITCH), Y(L.HEADER_Z));
    g.lineTo(X((i - 1.5) * PITCH * 1.6), Y(L.HEADER_Z + 3));
    g.lineTo(X((i - 1.5) * 1.2), Y(L.D / 2 - 1.5));
    g.stroke();
  }
  // Pads del conector
  g.fillStyle = '#d8d3c2';
  for (let i = 0; i < 4; i++) {
    g.beginPath();
    g.arc(X((i - 1.5) * PITCH), Y(L.HEADER_Z), 0.85, 0, Math.PI * 2);
    g.fill();
  }
  labels.forEach((l, i) => text(g, l, X((i - 1.5) * PITCH), Y(L.HEADER_Z + 2.05), { size: 0.95, color: SILK, weight: 700 }));
  return texture(c);
}

/** Módulo OLED con el conector arriba (0.96″ SSD1306 y 1.3″ SH1106): L = medidas de la definición. */
function buildOledModule(L, props, { labels, hole }) {
  const { W, D, PCB_Y, PCB_T, TOP, glass, active } = L;
  const b = new Builder();
  const hx = W / 2 - hole.inset;
  const hz = D / 2 - hole.inset;
  const holes = [[-hx, -hz, hole.r], [hx, -hz, hole.r], [-hx, hz, hole.r], [hx, hz, hole.r]];
  b.root.add(extrudeBoard(boardShape(W, D, { r: 0.6 }), PCB_T, oledSilk(L, labels), '#123577', { holes, w: W, d: D, y0: PCB_Y }));
  holeRings(b, holes, TOP);
  // Conector de 4 pines hacia arriba
  maleHeaderUp(b, [0, 1, 2, 3].map((i) => (i - 1.5) * PITCH), L.HEADER_Z, TOP, { pinH: L.PIN_Y - TOP });
  // Panel: vidrio superior, repisa con el driver (COG) y el flexible doblado sobre el borde
  const gy = TOP + 0.12; // cinta doble cara
  const ledge = 3.6;
  // El vidrio no se ilumina: su reflejo lo pone la capa aditiva de arriba (igual sobre la zona activa).
  const black = new THREE.MeshBasicMaterial({ color: '#000000' });
  b.box(glass.w, glass.t, glass.d - ledge, black, 0, gy, glass.z0 + (glass.d - ledge) / 2);
  b.box(glass.w, glass.t * 0.5, ledge, mat('#101214', { roughness: 0.2, metalness: 0.2 }), 0, gy, glass.z0 + glass.d - ledge / 2);
  b.box(15.5, 0.35, 1.1, M.chip, 0, gy + glass.t * 0.5, glass.z0 + glass.d - ledge / 2 - 0.4);
  const fpc = mat('#7a3f12', { roughness: 0.4 });
  const z1 = glass.z0 + glass.d;
  b.box(12, 0.12, D / 2 - z1 + 0.2, fpc, 0, TOP + 0.02, (z1 + D / 2) / 2);
  b.box(12, PCB_T + 0.4, 0.14, fpc, 0, PCB_Y - 0.2, D / 2 + 0.07);
  b.build();

  return oledPanel(b, new OledPainter(props.color), {
    active, x: 0, z: active.z, top: gy + glass.t, glassW: glass.w, glassD: glass.d - ledge, glassX: 0, glassZ: glass.z0 + (glass.d - ledge) / 2,
  });
}

export function buildOled(def, props) {
  return buildOledModule(OLED, props, { labels: ['GND', 'VCC', 'SCL', 'SDA'], hole: { inset: 2.0, r: 1.0 } });
}

export function buildOled13(def, props) {
  return buildOledModule(OLED13, props, { labels: ['VDD', 'GND', 'SCK', 'SDA'], hole: { inset: 2.5, r: 1.5 } });
}

function oled32Silk() {
  const { W, D } = OLED32;
  const { c, g } = canvas(W, D, 24);
  const X = (x) => x + W / 2;
  const Y = (z) => z + D / 2;
  g.fillStyle = '#163f8f';
  g.fillRect(0, 0, W, D);
  g.fillStyle = '#d8d3c2';
  for (let i = 0; i < 4; i++) {
    g.beginPath();
    g.arc(X(OLED32.HEADER_X), Y((i - 1.5) * PITCH), 0.85, 0, Math.PI * 2);
    g.fill();
  }
  ['GND', 'VCC', 'SCK', 'SDA'].forEach((l, i) => text(g, l, X(OLED32.HEADER_X + 1.55), Y((i - 1.5) * PITCH), { size: 0.75, color: SILK, weight: 700, align: 'left' }));
  return texture(c);
}

export function buildOled32(def, props) {
  const { W, D, PCB_Y, PCB_T, TOP, glass, active } = OLED32;
  const b = new Builder();
  b.root.add(extrudeBoard(boardShape(W, D, { r: 0.5 }), PCB_T, oled32Silk(), '#123577', { w: W, d: D, y0: PCB_Y }));
  // Conector de 4 pines hacia arriba en el borde izquierdo
  b.box(PITCH, 2.5, 4 * PITCH, M.blackPlastic, OLED32.HEADER_X, TOP, 0);
  for (let i = 0; i < 4; i++) b.box(0.64, OLED32.PIN_Y - TOP, 0.64, M.gold, OLED32.HEADER_X, TOP, (i - 1.5) * PITCH);
  // Vidrio, repisa con el driver y flexible doblado sobre el borde derecho
  const gy = TOP + 0.12;
  const ledge = 3.0;
  const black = new THREE.MeshBasicMaterial({ color: '#000000' });
  b.box(glass.w - ledge, glass.t, glass.d, black, glass.x0 + (glass.w - ledge) / 2, gy, 0);
  b.box(ledge, glass.t * 0.5, glass.d, mat('#101214', { roughness: 0.2, metalness: 0.2 }), glass.x0 + glass.w - ledge / 2, gy, 0);
  b.box(1.0, 0.35, 8.5, M.chip, glass.x0 + glass.w - ledge / 2 - 0.3, gy + glass.t * 0.5, 0);
  const fpc = mat('#7a3f12', { roughness: 0.4 });
  const x1 = glass.x0 + glass.w;
  b.box(W / 2 - x1 + 0.2, 0.12, 7, fpc, (x1 + W / 2) / 2, TOP + 0.02, 0);
  b.box(0.14, PCB_T + 0.4, 7, fpc, W / 2 + 0.07, PCB_Y - 0.2, 0);
  b.build();
  return oledPanel(b, new OledPainter(props.color, 32), {
    active, x: active.x, z: 0, top: gy + glass.t, glassW: glass.w - ledge, glassD: glass.d, glassX: glass.x0 + (glass.w - ledge) / 2, glassZ: 0,
  });
}

// 7 segmentos ------------------------------------------------------------------------------------------
function segOffColor(hex) {
  return '#' + new THREE.Color('#d7d3ca').lerp(new THREE.Color(hex), 0.12).getHexString();
}

/** Dígitos de 7 segmentos sobre una cara: devuelve la paleta (índice = dígito × 8 + segmento). */
function segmentDigits(b, centers, y, z, { h, w, t, color, dp = true, colon = null }) {
  const pal = new Palette(centers.length * 8 + (colon ? 1 : 0), { color: segOffColor(color), roughness: 0.5 });
  const shapes = digitShapes({ h, w, t, dp });
  centers.forEach((x, d) => shapes.forEach((s, i) => pal.add(flatShape(s, x, y, z), d * 8 + i)));
  if (colon) {
    // Dos puntos del reloj: un solo elemento (bit 7 del segundo dígito)
    const g1 = flatDisc(colon.r, colon.x + colon.dz * 0.17, y, z - colon.dz);
    const g2 = flatDisc(colon.r, colon.x - colon.dz * 0.17, y, z + colon.dz);
    pal.add(g1, centers.length * 8);
    pal.add(g2, centers.length * 8);
  }
  b.root.add(pal.mesh());
  return pal;
}

function sevenSegmentView(def, props, digits) {
  const S = digits === 1 ? SEG7 : SEG7X4;
  const color = (LED_COLORS[props.color] || LED_COLORS.red).hex;
  const b = new Builder();
  const top = S.Y0 + S.H;
  b.box(S.W, S.H - 0.06, S.D, BODY.white, 0, S.Y0, 0);
  b.box(S.W - 0.3, 0.06, S.D - 0.3, BODY.face, 0, top - 0.06, 0);
  for (const p of getPins(def.type, props)) b.box(0.5, S.Y0 + 0.4, 0.28, M.tin, p.x, 0, p.z);
  b.build();
  const centers = digits === 1 ? [-0.9] : [0, 1, 2, 3].map((d) => (d - 1.5) * S.PITCH_X - 0.9);
  const pal = segmentDigits(b, centers, top + 0.01, 0, { h: 12.3, w: 6.0, t: 1.75, color });
  const lit = linear(color);
  return paletteView(b, pal, (v) => {
    const seg = v.seg || [];
    for (let i = 0; i < digits * 8; i++) pal.set(i, lit, seg[i] || 0);
  });
}

// TM1637 ---------------------------------------------------------------------------------------------
function tmSilk() {
  const { W, D } = TM1637;
  const { c, g } = canvas(W, D, 20);
  const X = (x) => x + W / 2;
  const Y = (z) => z + D / 2;
  g.fillStyle = '#1752b5';
  g.fillRect(0, 0, W, D);
  g.strokeStyle = 'rgba(10,40,120,0.7)';
  g.lineWidth = 0.35;
  for (let i = 0; i < 4; i++) {
    g.beginPath();
    g.moveTo(X(W / 2 - 3.5), Y((i - 1.5) * PITCH));
    g.lineTo(X(W / 2 - 6), Y((i - 1.5) * PITCH * 1.4));
    g.lineTo(X(TM1637.display.x + TM1637.display.w / 2 + 0.6), Y((i - 1.5) * 2.2));
    g.stroke();
  }
  ['CLK', 'DIO', 'VCC', 'GND'].forEach((l, i) => text(g, l, X(W / 2 - PITCH - 1.1), Y((i - 1.5) * PITCH), { size: 1.25, color: SILK, weight: 700, align: 'right' }));
  text(g, 'TM1637', X(TM1637.display.x), Y(D / 2 - 2.1), { size: 1.3, color: SILK, weight: 700 });
  return texture(c);
}

export function buildTm1637(def, props) {
  const { W, D, PCB_Y, PCB_T, TOP, display } = TM1637;
  const color = (LED_COLORS[props.color] || LED_COLORS.red).hex;
  const b = new Builder();
  const hx = W / 2 - 2.6;
  const hz = D / 2 - 2.6;
  const holes = [[-hx, -hz, 1.1], [hx, -hz, 1.1], [-hx, hz, 1.1], [hx, hz, 1.1]];
  b.root.add(extrudeBoard(boardShape(W, D, { r: 0.8 }), PCB_T, tmSilk(), '#123f8c', { holes, w: W, d: D, y0: PCB_Y }));
  holeRings(b, holes, TOP);
  // Display de 0.36″ (blanco por los lados, cara negra)
  const dy = TOP + 0.4;
  b.box(display.w, display.h - 0.06, display.d, BODY.white, display.x, dy, 0);
  b.box(display.w - 0.3, 0.06, display.d - 0.3, BODY.face, display.x, dy + display.h - 0.06, 0);
  // Patas del display soldadas (se ven por los lados)
  for (let i = 0; i < 6; i++) for (const s of [-1, 1]) b.box(0.45, 0.4, 0.3, M.tin, display.x + (i - 2.5) * PITCH, TOP, s * (display.d / 2 - 1));
  rightAngleHeader(b, { axis: 'x', dir: 1, along: [0, 1, 2, 3].map((i) => (i - 1.5) * PITCH), edge: W / 2, tip: TM1637.PIN_X, pinY: TM1637.PIN_Y, pcbY: TOP });
  b.build();
  const clock = props.dots !== 'decimal';
  const centers = clock ? [-10.9, -4.55, 4.55, 10.9] : [-9.6, -3.2, 3.2, 9.6];
  const pal = segmentDigits(b, centers.map((x) => x + display.x - 0.4), dy + display.h + 0.01, 0, {
    h: 8.0, w: 3.9, t: 1.15, color, dp: !clock, colon: clock ? { x: display.x - 0.4, dz: 1.7, r: 0.55 } : null,
  });
  const lit = linear(color);
  return paletteView(b, pal, (v) => {
    const dig = v.dig || [];
    const level = v.on ? v.level : 0;
    for (let d = 0; d < 4; d++) {
      for (let s = 0; s < 8; s++) {
        const on = (dig[d] >> s) & 1 && (s < 7 || !clock);
        pal.set(d * 8 + s, lit, on ? level : 0);
      }
    }
    if (clock) pal.set(32, lit, dig[1] & 0x80 ? level : 0);
  });
}

// MAX7219 (FC-16) -----------------------------------------------------------------------------------
const DOT_PITCH = 4.0;

function fc16Silk(modules) {
  const W = MAX7219.CELL * modules;
  const D = MAX7219.CELL;
  const { c, g } = canvas(W, D, 12);
  const X = (x) => x + W / 2;
  const Y = (z) => z + D / 2;
  g.fillStyle = '#1b4fa3';
  g.fillRect(0, 0, W, D);
  const lab = ['VCC', 'GND', 'DIN', 'CS', 'CLK'];
  const out = ['VCC', 'GND', 'DOUT', 'CS', 'CLK'];
  lab.forEach((l, i) => text(g, l, X(W / 2 - PITCH - 1.4), Y((i - 2) * PITCH), { size: 1.2, color: SILK, weight: 700, align: 'right' }));
  out.forEach((l, i) => text(g, l, X(-W / 2 + PITCH + 1.4), Y((i - 2) * PITCH), { size: 1.2, color: SILK, weight: 700, align: 'left' }));
  for (let m = 0; m < modules; m++) {
    const cx = X((m - (modules - 1) / 2) * MAX7219.CELL);
    text(g, 'FC-16', cx, Y(D / 2 - 2.4), { size: 1.6, color: SILK, weight: 800 });
    text(g, 'IN', cx + 9, Y(-D / 2 + 2.2), { size: 1.4, color: SILK, weight: 700 });
    text(g, '←', cx + 5.5, Y(-D / 2 + 2.2), { size: 1.6, color: SILK, weight: 700 });
    text(g, 'OUT', cx - 9, Y(-D / 2 + 2.2), { size: 1.4, color: SILK, weight: 700 });
  }
  return texture(c);
}

function max7219View(def, props, modules) {
  const { CELL, PCB_Y, PCB_T, TOP, SOCKET_H, MATRIX_H } = MAX7219;
  const W = CELL * modules;
  const b = new Builder();
  b.root.add(extrudeBoard(boardShape(W, CELL, { r: 0.5 }), PCB_T, fc16Silk(modules), '#163f86', { w: W, d: CELL, y0: PCB_Y }));
  const matY = TOP + SOCKET_H;
  const socket = mat('#141517', { roughness: 0.6 });
  for (let m = 0; m < modules; m++) {
    const cx = (m - (modules - 1) / 2) * CELL;
    // Zócalos de la matriz, el MAX7219 (SOIC-24) y sus capacitores debajo
    for (const s of [-1, 1]) b.box(8 * PITCH, SOCKET_H, PITCH, socket, cx, TOP, s * (CELL / 2 - 3.2));
    b.box(15.4, 2.4, 7.5, M.chip, cx, TOP, -2);
    b.box(3.2, 1.6, 1.6, mat('#6b4b2a', { roughness: 0.5 }), cx + 9, TOP, 4);
    b.box(2.0, 0.6, 1.25, mat('#c9b48c', { roughness: 0.6 }), cx - 9, TOP, 4);
    // Cuerpo de la matriz 1088AS
    b.box(CELL - 0.3, MATRIX_H - 0.06, CELL - 0.3, mat('#18191b', { roughness: 0.5 }), cx, matY, 0);
    b.box(CELL - 0.5, 0.06, CELL - 0.5, mat('#0e0f10', { roughness: 0.45 }), cx, matY + MATRIX_H - 0.06, 0);
  }
  const along = [0, 1, 2, 3, 4].map((i) => (i - 2) * PITCH);
  const tip = W / 2 + MAX7219.PIN_OUT;
  rightAngleHeader(b, { axis: 'x', dir: 1, along, edge: W / 2, tip, pinY: MAX7219.PIN_Y, pcbY: TOP });
  rightAngleHeader(b, { axis: 'x', dir: -1, along, edge: -W / 2, tip: -tip, pinY: MAX7219.PIN_Y, pcbY: TOP });
  b.build();
  // 64 puntos por matriz; el chip k maneja la matriz k contando desde la derecha (entrada).
  const pal = new Palette(64 * modules, { color: '#ddd7cb', roughness: 0.4, peak: 1.05 });
  const y = matY + MATRIX_H + 0.01;
  const spots = [];
  for (let k = 0; k < modules; k++) {
    const cx = (modules - 1 - k - (modules - 1) / 2) * CELL;
    for (let r = 0; r < 8; r++) {
      for (let col = 0; col < 8; col++) {
        const x = cx + (col - 3.5) * DOT_PITCH;
        const z = (r - 3.5) * DOT_PITCH;
        pal.add(flatDisc(1.45, x, y, z, 16), k * 64 + r * 8 + col);
        spots.push([x, y + 0.8, z]);
      }
    }
  }
  b.root.add(pal.mesh());
  const halos = new Halos(spots, 8);
  b.root.add(halos.points);
  const red = linear('#ff2614');
  return paletteView(b, pal, (v) => {
    const chips = v.chips || [];
    for (let k = 0; k < modules; k++) {
      const c = chips[k];
      for (let r = 0; r < 8; r++) {
        const row = c ? c.rows[r] : 0;
        for (let col = 0; col < 8; col++) {
          const k2 = (row >> (7 - col)) & 1 ? c.level : 0;
          pal.set(k * 64 + r * 8 + col, red, k2);
          halos.set(k * 64 + r * 8 + col, red, k2 * 0.3);
        }
      }
    }
  }, halos);
}

// Display de 8 dígitos MAX7219 --------------------------------------------------------------------------
function max7segSilk() {
  const { W, D } = MAX7SEG;
  const { c, g } = canvas(W, D, 14);
  const X = (x) => x + W / 2;
  const Y = (z) => z + D / 2;
  g.fillStyle = '#1b4fa3';
  g.fillRect(0, 0, W, D);
  ['VCC', 'GND', 'DIN', 'CS', 'CLK'].forEach((l, i) => text(g, l, X(-W / 2 + PITCH + 1.3), Y((i - 2) * PITCH), { size: 1.1, color: SILK, weight: 700, align: 'left' }));
  ['VCC', 'GND', 'DOUT', 'CS', 'CLK'].forEach((l, i) => text(g, l, X(W / 2 - PITCH - 1.3), Y((i - 2) * PITCH), { size: 1.1, color: SILK, weight: 700, align: 'right' }));
  return texture(c);
}

export function buildMax7219SevenSeg(def, props) {
  const { W, D, PCB_Y, PCB_T, TOP, display } = MAX7SEG;
  const color = (LED_COLORS[props.color] || LED_COLORS.red).hex;
  const b = new Builder();
  b.root.add(extrudeBoard(boardShape(W, D, { r: 0.6 }), PCB_T, max7segSilk(), '#163f86', { w: W, d: D, y0: PCB_Y }));
  const dy = TOP + 0.4;
  const centers = [-1, 1].map((s) => s * (display.w + display.gap) / 2);
  for (const cx of centers) {
    b.box(display.w, display.h - 0.06, display.d, BODY.white, cx, dy, 0);
    b.box(display.w - 0.3, 0.06, display.d - 0.3, BODY.face, cx, dy + display.h - 0.06, 0);
  }
  const along = [0, 1, 2, 3, 4].map((i) => (i - 2) * PITCH);
  const tip = W / 2 + MAX7SEG.PIN_OUT;
  rightAngleHeader(b, { axis: 'x', dir: -1, along, edge: -W / 2, tip: -tip, pinY: MAX7SEG.PIN_Y, pcbY: TOP });
  rightAngleHeader(b, { axis: 'x', dir: 1, along, edge: W / 2, tip, pinY: MAX7SEG.PIN_Y, pcbY: TOP });
  b.build();
  // Dígitos de izquierda a derecha; el registro del dígito 0 maneja el de la derecha.
  const xs = [];
  for (const cx of centers) for (let k = 0; k < 4; k++) xs.push(cx + (k - 1.5) * 7.5 - 0.4);
  const pal = segmentDigits(b, xs, dy + display.h + 0.01, 0, { h: 8.0, w: 3.9, t: 1.15, color });
  const lit = linear(color);
  return paletteView(b, pal, (v) => {
    const c = v.chips && v.chips[0];
    for (let pos = 0; pos < 8; pos++) {
      const row = c ? c.rows[7 - pos] : 0;
      for (let i = 0; i < 8; i++) {
        // Bits del MAX7219: DP A B C D E F G (7..0); la paleta va A…G, DP.
        const bit = i < 7 ? 6 - i : 7;
        pal.set(pos * 8 + i, lit, (row >> bit) & 1 ? c.level : 0);
      }
    }
  });
}

// NeoPixel -------------------------------------------------------------------------------------------
/** LED 5050: cuerpo blanco con la lente redonda (emisión por paleta). */
function led5050(b, pal, i, x, z, ry = 0, spots = null) {
  if (spots) spots.push([x, NEO.TOP + 2.4, z]);
  b.box(NEO.LED, 1.5, NEO.LED, mat('#f3f1ea', { roughness: 0.45 }), x, NEO.TOP, z, { ry });
  // Esquina marcada (pata 1)
  const c = Math.cos(ry);
  const s = Math.sin(ry);
  const lx = -2.05;
  const lz = -2.05;
  b.box(0.7, 0.05, 0.7, M.chip, x + lx * c + lz * s, NEO.TOP + 1.5, z - lx * s + lz * c);
  pal.add(flatDisc(1.95, x, NEO.TOP + 1.52, z, 20), i);
}

function neoView(def, props) {
  const n = neoCount(def.type, props);
  const rgbw = neoRgbw(def.type, props);
  const b = new Builder();
  // A pleno brillo un WS2812 encandila: el blanco pasa el umbral del bloom y todos llevan halo. El LED blanco
  // del SK6812 lleva fósforo amarillo: apagado, el difusor se ve un poco más cálido.
  const pal = new Palette(n, { color: rgbw ? '#efe5c2' : '#ece6d4', roughness: 0.35, peak: 3.0 });
  const spots = [];
  const pcbMat = '#141516';
  if (def.type === 'neopixel-ring') {
    const s = NEO_RING_SIZES[props.pixels] || NEO_RING_SIZES[16];
    const shape = new THREE.Shape();
    shape.absarc(0, 0, s.od / 2, 0, Math.PI * 2, false);
    const hole = new THREE.Path();
    hole.absarc(0, 0, s.id / 2, 0, Math.PI * 2, true);
    shape.holes.push(hole);
    const geo = new THREE.ExtrudeGeometry(shape, { depth: NEO.PCB_T, bevelEnabled: false, curveSegments: 64 });
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, NEO.PCB_Y, 0);
    b.add(geo, mat(pcbMat, { roughness: 0.5 }));
    const r = (s.od + s.id) / 4;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      led5050(b, pal, i, r * Math.sin(a), -r * Math.cos(a), -a, spots);
    }
    rightAngleHeader(b, { axis: 'z', dir: 1, along: [0, 1, 2, 3].map((i) => (i - 1.5) * PITCH), edge: s.od / 2 - 0.4, tip: s.od / 2 + NEO.PIN_OUT, pinY: NEO.PIN_Y, pcbY: NEO.PCB_Y });
  } else {
    const isStick = def.type === 'neopixel-stick';
    const w = isStick ? NEO_STICK.w : NEO_MATRIX.w;
    const d = isStick ? NEO_STICK.d : NEO_MATRIX.d;
    b.root.add(extrudeBoard(boardShape(w, d, { r: isStick ? 1.2 : 1.5 }), NEO.PCB_T, neoSilk(def.type, w, d, rgbw), '#0c0c0d', { w, d, y0: NEO.PCB_Y }));
    if (isStick) {
      for (let i = 0; i < n; i++) led5050(b, pal, i, (i - 3.5) * NEO_STICK.pitch, 0, 0, spots);
    } else {
      for (let i = 0; i < n; i++) {
        const row = Math.floor(i / 8);
        let col = i % 8;
        if (props.layout === 'serpentine' && row % 2) col = 7 - col;
        led5050(b, pal, i, (col - 3.5) * NEO_MATRIX.pitch, (row - 3.5) * NEO_MATRIX.pitch, 0, spots);
      }
    }
    const along = [-1, 0, 1].map((k) => k * PITCH);
    rightAngleHeader(b, { axis: 'x', dir: -1, along, edge: -w / 2 + 0.4, tip: -(w / 2 + NEO.PIN_OUT), pinY: NEO.PIN_Y, pcbY: NEO.PCB_Y });
    rightAngleHeader(b, { axis: 'x', dir: 1, along, edge: w / 2 - 0.4, tip: w / 2 + NEO.PIN_OUT, pinY: NEO.PIN_Y, pcbY: NEO.PCB_Y });
  }
  b.build();
  b.root.add(pal.mesh());
  const halos = new Halos(spots, 11);
  b.root.add(halos.points);
  const chroma = new THREE.Color();
  const tint = new THREE.Color((NEO_WHITES[props.leds] || NEO_WHITES['rgbw-natural']).hex);
  const mix = new THREE.Color();
  return paletteView(b, pal, (v) => {
    const px = v.px || [];
    const white = v.w || [];
    for (let i = 0; i < n; i++) {
      const r = px[i * 3] || 0;
      const g = px[i * 3 + 1] || 0;
      const bl = px[i * 3 + 2] || 0;
      if (rgbw) {
        // Los cuatro LEDs suman su luz: el blanco aporta el tono del suyo (rojo + blanco da rosa, no solo más rojo)
        const w = (white[i] || 0) / 255;
        mix.setRGB(r / 255 + tint.r * w, g / 255 + tint.g * w, bl / 255 + tint.b * w);
        const m = Math.max(mix.r, mix.g, mix.b);
        if (m > 0) chroma.copy(mix).multiplyScalar(1 / m);
        const k = m > 0 ? perceived(Math.min(1, m)) : 0;
        pal.set(i, chroma, k);
        halos.set(i, chroma, k * 0.55);
        continue;
      }
      pal.setRGB(i, r, g, bl);
      const m = Math.max(r, g, bl);
      if (m > 0) chroma.setRGB(r / m, g / m, bl / m);
      halos.set(i, chroma, m > 0 ? perceived(m / 255) * 0.55 : 0);
    }
  }, halos);
}

function neoSilk(type, w, d, rgbw = false) {
  const { c, g } = canvas(w, d, 12);
  const X = (x) => x + w / 2;
  const Y = (z) => z + d / 2;
  g.fillStyle = '#141516';
  g.fillRect(0, 0, w, d);
  const ins = ['DIN', '5V', 'GND'];
  const outs = ['DOUT', '5V', 'GND'];
  const size = type === 'neopixel-stick' ? 0.9 : 1.3;
  ins.forEach((l, i) => text(g, l, X(-w / 2 + 0.8), Y((i - 1) * PITCH), { size, color: SILK, weight: 700, align: 'left' }));
  outs.forEach((l, i) => text(g, l, X(w / 2 - 0.8), Y((i - 1) * PITCH), { size, color: SILK, weight: 700, align: 'right' }));
  if (type === 'neopixel-matrix') text(g, rgbw ? 'SK6812 RGBW 8×8' : 'WS2812B 8×8', X(0), Y(d / 2 - 0.9), { size: 1.1, color: SILK, weight: 700 });
  return texture(c);
}

/** LED NeoPixel de 5 mm: domo difuso con 4 patas (la tercera, GND, es la más larga). */
export function buildNeoPixel5mm(def, props) {
  const b = new Builder();
  const y0 = 3.2;
  const xs = [-1.5, -0.5, 0.5, 1.5].map((k) => k * PITCH);
  xs.forEach((x, i) => {
    const top = [x * 0.42, y0, 0];
    if (i === 2) b.leg([top, [x, y0 - 1.3, 0], [x, 1.9, 0], [x + 0.5, 1.3, 0], [x, 0.7, 0], [x, 0, 0]], 0.24, M.tin);
    else b.leg([top, [x, y0 - 1.3, 0], [x, 0, 0]], 0.24, M.tin);
  });
  b.build();
  const material = new THREE.MeshStandardMaterial({ color: '#f5f3ee', emissive: '#000000', emissiveIntensity: 1, roughness: 0.5, transparent: true, opacity: 0.95 });
  const pts = [new THREE.Vector2(0, 0), new THREE.Vector2(2.9, 0), new THREE.Vector2(2.9, 1.0), new THREE.Vector2(2.5, 1.0), new THREE.Vector2(2.5, 5.6)];
  for (let i = 1; i <= 10; i++) {
    const a = (i / 10) * (Math.PI / 2);
    pts.push(new THREE.Vector2(2.5 * Math.cos(a), 5.6 + 2.5 * Math.sin(a)));
  }
  const dome = new THREE.Mesh(new THREE.LatheGeometry(pts, 28), material);
  dome.position.y = y0;
  dome.castShadow = true;
  b.root.add(dome);
  const flat = new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.0, 3.6), material);
  flat.position.set(-2.75, y0 + 0.5, 0);
  b.root.add(flat);
  const halo = glow('#ffffff', 22);
  halo.position.set(0, y0 + 5.6, 0);
  b.root.add(halo);
  const col = new THREE.Color();
  const tint = new THREE.Color();
  const milky = material.color.clone();
  const e = [0, 0, 0];
  return {
    root: b.root,
    update(v) {
      const px = v.px || [0, 0, 0];
      const d = Math.max(px[0], px[1], px[2]) / 255;
      // Los colores se mezclan según el ciclo de trabajo de cada LED; el brillo se percibe con curva.
      if (d > 0) col.setRGB(px[0] / 255 / d, px[1] / 255 / d, px[2] / 255 / d);
      const m = perceived(d);
      ledEmission(col, m, e, 2.8);
      material.emissive.setRGB(e[0], e[1], e[2]);
      material.emissiveIntensity = 1;
      tint.copy(col).multiplyScalar(0.45);
      material.color.copy(milky).lerp(tint, Math.min(1, m * 2));
      halo.material.color.copy(col);
      setGlow(halo, m);
    },
    reset() {
      this.update({ px: [0, 0, 0] });
    },
  };
}

// Barra de 10 LEDs ------------------------------------------------------------------------------------
export function buildLedBar(def, props) {
  const colors = (BAR_COLORS[props.color] || BAR_COLORS.red).leds;
  const b = new Builder();
  const top = BAR.Y0 + BAR.H;
  b.box(BAR.W, BAR.H - 0.06, BAR.D, mat('#26272a', { roughness: 0.55 }), 0, BAR.Y0, 0);
  b.box(BAR.W - 0.3, 0.06, BAR.D - 0.3, BODY.face, 0, top - 0.06, 0);
  for (const p of getPins(def.type, props)) b.box(0.5, BAR.Y0 + 0.4, 0.28, M.tin, p.x, 0, p.z);
  // Marca de la pata 1 (esquina inferior izquierda)
  b.box(1.2, 0.07, 1.2, mat('#d9d6cf', { roughness: 0.5 }), -BAR.W / 2 + 1.0, top - 0.04, BAR.D / 2 - 1.0, { ry: Math.PI / 4 });
  b.build();
  const pal = new Palette(10, { color: '#d9d4ca', roughness: 0.5 });
  for (let i = 0; i < 10; i++) {
    const g = new THREE.PlaneGeometry(1.7, 5.2).rotateX(-Math.PI / 2);
    g.translate((i - 4.5) * PITCH, top + 0.01, 0);
    pal.add(g, i);
  }
  b.root.add(pal.mesh());
  const lit = colors.map((c) => linear(LED_COLORS[c].hex));
  return paletteView(b, pal, (v) => {
    const seg = v.seg || [];
    for (let i = 0; i < 10; i++) pal.set(i, lit[i], seg[i] || 0);
  });
}

export const MODELS = {
  'oled-ssd1306': buildOled,
  'oled-128x32': buildOled32,
  'oled-sh1106': buildOled13,
  'seven-segment': (def, props) => sevenSegmentView(def, props, 1),
  'seven-segment-4': (def, props) => sevenSegmentView(def, props, 4),
  tm1637: buildTm1637,
  'max7219-matrix': (def, props) => max7219View(def, props, 1),
  'max7219-matrix4': (def, props) => max7219View(def, props, 4),
  'max7219-7seg': buildMax7219SevenSeg,
  neopixel: buildNeoPixel5mm,
  'neopixel-ring': neoView,
  'neopixel-stick': neoView,
  'neopixel-matrix': neoView,
  'led-bar': buildLedBar,
};
